/**
 * SYSTEM SETTINGS MODULE — Shipping Carriers.
 * Per-tenant UPS connection: credentials (stored server-side only), ship-from
 * origin, and label defaults. The POS Orders / Product Sample Orders screens use
 * this to auto-create UPS labels via the `create-shipping-label` edge function.
 * Workspace owner/admin only (route is RequireWorkspaceAdmin).
 */

import { useEffect, useState } from "react";
import {
  getCarrierSettings,
  saveCarrier,
  testCarrier,
  disconnectCarrier,
} from "../utils/shippingCarrierService.js";
import "./ShippingCarriers.css";

const SERVICE_CODES = [
  { code: "03", label: "Ground" },
  { code: "02", label: "2nd Day Air" },
  { code: "12", label: "3 Day Select" },
  { code: "13", label: "Next Day Air Saver" },
  { code: "01", label: "Next Day Air" },
  { code: "14", label: "Next Day Air Early" },
  { code: "59", label: "2nd Day Air A.M." },
];

const EMPTY = {
  environment: "sandbox",
  enabled: false,
  account_number: "",
  from_name: "",
  from_phone: "",
  from_street: "",
  from_city: "",
  from_state: "",
  from_postal: "",
  from_country: "US",
  default_service_code: "03",
  default_packaging_code: "02",
  default_weight_lbs: "1",
  default_length_in: "",
  default_width_in: "",
  default_height_in: "",
  default_label_format: "PDF",
};

export default function ShippingCarriersSection() {
  const [form, setForm] = useState(EMPTY);
  const [hasCreds, setHasCreds] = useState(false);
  const [editCreds, setEditCreds] = useState(false);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState(null); // { kind: 'ok'|'err', text }

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    (async () => {
      try {
        const s = await getCarrierSettings("ups");
        if (s) {
          setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, s[k] ?? EMPTY[k]])) });
          setHasCreds(!!s.has_credentials);
          setEditCreds(!s.has_credentials);
        } else {
          setEditCreds(true);
        }
      } catch (e) {
        setMsg({ kind: "err", text: e.message });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Build the payload; only send credentials when the admin entered new ones.
  function payload(extra = {}) {
    const p = { ...form, ...extra };
    if (editCreds && clientId && clientSecret) {
      p.clientId = clientId;
      p.clientSecret = clientSecret;
    }
    return p;
  }

  async function handleTest() {
    setTesting(true);
    setMsg(null);
    try {
      await testCarrier(payload());
      setMsg({ kind: "ok", text: "UPS credentials verified." });
    } catch (e) {
      setMsg({ kind: "err", text: e.message });
    } finally {
      setTesting(false);
    }
  }

  async function handleSave() {
    setBusy(true);
    setMsg(null);
    try {
      const res = await saveCarrier(payload());
      setHasCreds(!!res.has_credentials);
      if (res.has_credentials) { setEditCreds(false); setClientId(""); setClientSecret(""); }
      setMsg({ kind: "ok", text: form.enabled ? "Saved. UPS labels are enabled." : "Saved." });
    } catch (e) {
      setMsg({ kind: "err", text: e.message });
    } finally {
      setBusy(false);
    }
  }

  async function handleDisconnect() {
    if (!confirm("Remove the UPS credentials and disable label creation?")) return;
    setBusy(true);
    setMsg(null);
    try {
      await disconnectCarrier("ups");
      setHasCreds(false);
      setEditCreds(true);
      set("enabled", false);
      setMsg({ kind: "ok", text: "UPS disconnected." });
    } catch (e) {
      setMsg({ kind: "err", text: e.message });
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <section className="settings-card wide"><p className="muted">Loading shipping settings…</p></section>;

  return (
    <section className="settings-card wide ship-carriers">
      <h2 style={{ marginTop: 0 }}>Shipping Carriers</h2>
      <p className="muted">
        Connect your UPS account to create shipping labels straight from a POS
        Order or Product Sample Order. Labels ship on <strong>your</strong> UPS
        account and bill to it. Start in <strong>Sandbox</strong> to test, then
        switch to Production when you’re ready.
      </p>

      <div className="ship-brandrow">
        <span className="ship-badge">UPS</span>
        <span className={`ship-status ${hasCreds && form.enabled ? "on" : "off"}`}>
          {hasCreds ? (form.enabled ? "Connected · enabled" : "Connected · disabled") : "Not connected"}
        </span>
      </div>

      {msg && <div className={`ship-msg ${msg.kind}`}>{msg.text}</div>}

      {/* Environment + credentials */}
      <h3>Account</h3>
      <div className="ship-grid">
        <label>
          <span>Environment</span>
          <select value={form.environment} onChange={(e) => set("environment", e.target.value)}>
            <option value="sandbox">Sandbox (testing)</option>
            <option value="production">Production (live)</option>
          </select>
        </label>
        <label>
          <span>UPS Account Number</span>
          <input value={form.account_number} onChange={(e) => set("account_number", e.target.value)} placeholder="e.g. A1B2C3" />
        </label>
      </div>

      {hasCreds && !editCreds ? (
        <div className="ship-creds-set">
          <span>API credentials are on file <span className="ship-dim">•••• stored securely</span></span>
          <button type="button" className="btn-link" onClick={() => setEditCreds(true)}>Replace</button>
        </div>
      ) : (
        <div className="ship-grid">
          <label>
            <span>UPS Client ID</span>
            <input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="Client ID" autoComplete="off" />
          </label>
          <label>
            <span>UPS Client Secret</span>
            <input type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder="Client Secret" autoComplete="off" />
          </label>
        </div>
      )}

      {/* Ship-from origin */}
      <h3>Ship From (origin)</h3>
      <div className="ship-grid">
        <label><span>Name / Company</span><input value={form.from_name} onChange={(e) => set("from_name", e.target.value)} /></label>
        <label><span>Phone</span><input value={form.from_phone} onChange={(e) => set("from_phone", e.target.value)} /></label>
      </div>
      <label className="ship-full"><span>Street Address</span><input value={form.from_street} onChange={(e) => set("from_street", e.target.value)} /></label>
      <div className="ship-grid ship-grid-3">
        <label><span>City</span><input value={form.from_city} onChange={(e) => set("from_city", e.target.value)} /></label>
        <label><span>State</span><input value={form.from_state} onChange={(e) => set("from_state", e.target.value)} maxLength={2} placeholder="TX" /></label>
        <label><span>ZIP</span><input value={form.from_postal} onChange={(e) => set("from_postal", e.target.value)} placeholder="78701" /></label>
      </div>

      {/* Label defaults */}
      <h3>Label Defaults</h3>
      <div className="ship-grid ship-grid-3">
        <label>
          <span>Service</span>
          <select value={form.default_service_code} onChange={(e) => set("default_service_code", e.target.value)}>
            {SERVICE_CODES.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
          </select>
        </label>
        <label>
          <span>Label format</span>
          <select value={form.default_label_format} onChange={(e) => set("default_label_format", e.target.value)}>
            <option value="PDF">PDF (laser printer)</option>
            <option value="ZPL">ZPL (thermal printer)</option>
            <option value="GIF">GIF (image)</option>
          </select>
        </label>
        <label><span>Default weight (lbs)</span><input type="number" min="0" step="0.1" value={form.default_weight_lbs} onChange={(e) => set("default_weight_lbs", e.target.value)} /></label>
      </div>
      <div className="ship-grid ship-grid-3">
        <label><span>Default length (in)</span><input type="number" min="0" value={form.default_length_in} onChange={(e) => set("default_length_in", e.target.value)} /></label>
        <label><span>Default width (in)</span><input type="number" min="0" value={form.default_width_in} onChange={(e) => set("default_width_in", e.target.value)} /></label>
        <label><span>Default height (in)</span><input type="number" min="0" value={form.default_height_in} onChange={(e) => set("default_height_in", e.target.value)} /></label>
      </div>

      <label className="ship-enable">
        <input type="checkbox" checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} />
        <span>Enable UPS label creation on orders</span>
      </label>

      <div className="ship-actions">
        <button type="button" className="btn-secondary" onClick={handleTest} disabled={testing || busy}>
          {testing ? "Testing…" : "Test connection"}
        </button>
        <button className="btn" type="button" onClick={handleSave} disabled={busy}>
          {busy ? "Saving…" : "Save"}
        </button>
        {hasCreds && (
          <button type="button" className="btn-danger" onClick={handleDisconnect} disabled={busy}>Disconnect</button>
        )}
      </div>
    </section>
  );
}
